import { CheckOutlined } from '@mui/icons-material';
import { Badge, Box, Button, Grid, useMediaQuery, useTheme } from '@mui/material';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppCustomIcon from '../../../../app/features/icon/AppIcon';
import CAppDialog from '../../../../app/components/CAppDialog';
import CAppFilterSelect from '../../../../app/components/CAppFilterSelect';
import investmentFilterLang from '../../../investor/features/fund-type/langs/investmentFilterLang';
import IFund from '../../../investor/interfaces/fund/IFund';
import IDocumentsFilters from '../../interfaces/IDocumentsFilters';
import langDocumentFilesFilters from '../../langs/langDocumentFilesFilters';
import IDocumentFile from '../../interfaces/IDocumentFile';
import useInvestExpertises from '../../../investor/hooks/expertise/useInvestExpertises';
import useDocumentTypes from '../../hooks/useDocumentTypes';
import langDocumentType from '../../langs/langDocumentType';
import { langLookup } from '../../../../app/utils/langLookup';
import CAppInfoTooltip from '../../../../app/components/CAppInfoTooltip';
import investmentFundLang from '../../../investor/langs/investmentFundLang';
import IInvestor from '../../../investor/interfaces/IInvestor';
import IFundInvestor from '../../../investor/interfaces/IFundInvestor';
import CAppFormTextField from '../../../../app/components/Form/CAppFormTextField';
import langDocumentFiles from '../../langs/langDocumentFiles';
import { DelimitedNumericArrayParam, StringParam, useQueryParams } from 'use-query-params';

type FDocumentFiltersProps = {
	allFunds?: IFund[];
	allDocuments: IDocumentFile[];
	filters: IDocumentsFilters;
	orderFilters: { filterTag: string; xs: number; md: number }[];
	onChange: (filters: IDocumentsFilters) => void;
};

const FDocumentFilters = (props: FDocumentFiltersProps) => {
	const { allFunds, allDocuments, filters, onChange, orderFilters } = props;
	const { t } = useTranslation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const fundTypes = useInvestExpertises();
	const { documentTypes } = useDocumentTypes();

	const [nbMoreFilters, setNbMoreFilters] = useState<number>(0);
	useEffect(() => {
		let nbMoreFiltersTmp = 0;
		if (filters) {
			if (filters.fundVintage !== undefined && filters.fundVintage !== null) {
				nbMoreFiltersTmp += 1;
			}
			if (filters.format !== undefined && filters.format !== null) {
				nbMoreFiltersTmp += 1;
			}
		}
		setNbMoreFilters(nbMoreFiltersTmp);
	}, [filters]);

	const [openFilters, setOpenFilters] = useState(false);

	const componentFilterFundSelect = (
		<CAppFilterSelect
			className="filter-funds"
			multiple
			multipleRenderTags={(val: IFund[]) => (
				<Box style={{ paddingLeft: '5px', fontSize: '0.85rem' }}>
					{t(investmentFilterLang.x_funds_private_credit.tag, { count: val ? val.length : 0 })}
				</Box>
			)}
			value={
				allFunds ? allFunds.filter((x) => filters && filters.fundIds && filters.fundIds.includes(x.id)) : null
			}
			onChange={(val: IFund[]) => {
				onChange({
					...filters,
					fundIds: val && val.length > 0 ? val.map((x) => x.id) : (allFunds ?? []).map((x) => x.id),
				});
			}}
			label={t(langDocumentFilesFilters.search_for_funds.tag)}
			size="small"
			options={
				allFunds
					? allFunds.map((x: IFund) => ({
							id: x.id,
							name: x.name,
							color: x.isRealized ? '#8799B4' : null,
					  }))
					: null
			}
		/>
	);

	// Options sub portfolio
	const [optionsInvestors, setOptionsInvestors] = useState<{ id: number; name: string }[] | null>(null);
	useEffect(() => {
		if (allFunds) {
			const investorsTmp = _.unionBy(
				_.flatten(
					allFunds.map((f) =>
						(f.investors ?? []).map((x: IFundInvestor) => ({
							id: x.company.id,
							name: x.company.name,
						})),
					),
				),
				'id',
			);
			setOptionsInvestors(_.orderBy(investorsTmp, ['name'], ['asc']));
		} else {
			setOptionsInvestors(null);
		}
	}, [allFunds]);

	const componentFilterInvestorSelect = (
		<CAppFilterSelect
			className="filter-subportfolio"
			multiple
			multipleRenderTags={(val: IFund[]) => (
				<Box style={{ paddingLeft: '5px', fontSize: '0.85rem' }}>
					{t(investmentFundLang.x_sub_portfolios.tag, { count: val ? val.length : 0 })}
				</Box>
			)}
			label={
				<>
					{t(investmentFundLang.label_all_sub_portfolios.tag)}
					<CAppInfoTooltip title={t(investmentFundLang.label_all_sub_portfolios_tooltip.tag)} />
				</>
			}
			value={
				optionsInvestors && filters
					? optionsInvestors.filter((x) => filters.investorIds && filters.investorIds.includes(x.id))
							.length === 0
						? optionsInvestors
						: optionsInvestors.filter((x) => filters.investorIds && filters.investorIds.includes(x.id))
					: null
			}
			onChange={(val: IInvestor[]) => {
				onChange({
					...filters,
					investorIds:
						val && val.length > 0 ? val.map((x) => x.id) : (optionsInvestors ?? []).map((x) => x.id),
				});
			}}
			size="small"
			options={optionsInvestors}
		/>
	);

	const [optionsDocumentTypes, setOptionsDocumentTypes] = useState<{ id: string; name: string }[] | null>(null);
	useEffect(() => {
		if (allDocuments) {
			setOptionsDocumentTypes(
				_.orderBy(
					_.uniqBy(
						allDocuments.map((x: IDocumentFile) => {
							const documentType = documentTypes.find((t) => t.tag === x.documentType);
							const tag = documentType ? langLookup(langDocumentType, documentType.name)?.tag : undefined;
							return {
								id: x.documentType,
								name: tag ? t(tag) : '',
							};
						}),
						'id',
					),
					['name'],
					['asc'],
				),
			);
		} else {
			setOptionsDocumentTypes(null);
		}
	}, [allDocuments, documentTypes, t]);

	const componentFilterDocumentTypes = (
		<CAppFilterSelect
			className="filter-document-type"
			value={optionsDocumentTypes ? optionsDocumentTypes.find((x) => x.id === filters.documentType) : null}
			onChange={(val: { id: string | null; name: string } | null) =>
				onChange({
					...filters,
					documentType: val ? val.id : null,
				})
			}
			label={t(langDocumentFilesFilters.document_type.tag)}
			size="small"
			options={optionsDocumentTypes}
		/>
	);

	const [optionsFundTypes, setOptionsFundTypes] = useState<{ id: string | undefined; name: string }[] | null>(null);
	useEffect(() => {
		if (allFunds && fundTypes) {
			setOptionsFundTypes(
				_.uniqBy(
					allFunds.map((x: IFund) => {
						const cFundType = fundTypes.find((t) => t.tag === x.typeTag);
						return {
							id: x.typeTag ?? undefined,
							name: cFundType?.name ?? '',
						};
					}),
					'id',
				),
			);
		} else {
			setOptionsFundTypes(null);
		}
	}, [allFunds, fundTypes, t]);

	const componentFilterFundTypes = (
		<CAppFilterSelect
			className="filter-fund-type"
			value={optionsFundTypes ? optionsFundTypes.find((x) => x.id === filters.fundType) : null}
			onChange={(val: { id: string | null; name: string } | null) =>
				onChange({
					...filters,
					fundType: val ? val.id : null,
				})
			}
			label={t(langDocumentFilesFilters.expertises.tag)}
			size="small"
			options={optionsFundTypes}
		/>
	);

	const optionsReleasePeriods = useMemo(
		() => [
			{
				id: '1_month',
				name: t(langDocumentFilesFilters.release_period_1_month.tag),
			},
			{
				id: '3_months',
				name: t(langDocumentFilesFilters.release_period_3_months.tag),
			},
			{
				id: '6_months',
				name: t(langDocumentFilesFilters.release_period_6_months.tag),
			},
			{
				id: '1_year',
				name: t(langDocumentFilesFilters.release_period_1_year.tag),
			},
			{
				id: null,
				name: t(langDocumentFilesFilters.release_period_all.tag),
			},
		],
		[t],
	);

	const componentFilterReleasePeriods = (
		<CAppFilterSelect
			className="filter-release-period"
			value={optionsReleasePeriods ? optionsReleasePeriods.find((x) => x.id === filters.releasePeriod) : null}
			onChange={(val: { id: string | null; name: string } | null) =>
				onChange({
					...filters,
					releasePeriod: val ? val.id : null,
				})
			}
			label={t(langDocumentFilesFilters.release_period.tag)}
			size="small"
			options={optionsReleasePeriods}
		/>
	);

	const componentFilterKeywords = (
		<CAppFormTextField
			className="filter-keywords"
			label={t(langDocumentFiles.search_autocomplete_placeholder.tag)}
			value={filters.keywords}
			fullWidth
			onChange={(e) =>
				onChange({
					...filters,
					keywords: e ? e.target.value : null,
				})
			}
		/>
	);

	const renderFilters = () => {
		return (
			<>
				{orderFilters.map((orderFilter, pos) => {
					let component = null;
					switch (orderFilter.filterTag) {
						case 'keywords':
							component = componentFilterKeywords;
							break;
						case 'investorIds':
							component = componentFilterInvestorSelect;
							break;
						case 'documentType':
							component = componentFilterDocumentTypes;
							break;
						case 'releasePeriod':
							component = componentFilterReleasePeriods;
							break;
						case 'fundType':
							component = componentFilterFundTypes;
							break;
						case 'fundIds':
							component = componentFilterFundSelect;
							break;
					}
					return (
						<Grid key={pos} item xs={orderFilter.xs} md={orderFilter.md}>
							{component}
						</Grid>
					);
				})}
			</>
		);
	};

	// Déclaration du format des query params (url)
	const queryParamsDefaultValue = {
		keywords: StringParam,
		fundIds: DelimitedNumericArrayParam,
		documentType: StringParam,
		releasePeriod: StringParam,
		investorIds: DelimitedNumericArrayParam,
		fundType: StringParam,
	};

	const [queryParams, setQueryParams] = useQueryParams(queryParamsDefaultValue);

	// Mise à jour des filtres lorsque l'on arrive sur la page
	useEffect(() => {
		if (queryParams) {
			onChange({
				keywords: queryParams.keywords,
				fundIds: queryParams.fundIds,
				documentType: queryParams.documentType,
				releasePeriod: queryParams.releasePeriod,
				investorIds: queryParams.investorIds,
				fundType: queryParams.fundType,
			} as IDocumentsFilters);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Mise à jour du query params quand on change les filtres
	useEffect(() => {
		if (filters) {
			setQueryParams({
				keywords: filters.keywords,
				fundIds: filters.fundIds,
				documentType: filters.documentType,
				releasePeriod: filters.releasePeriod,
				investorIds: filters.investorIds,
				fundType: filters.fundType,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filters]);

	return (
		<>
			<Box>
				<Grid
					container
					spacing={3}
					alignItems="center"
					style={{ marginTop: '0px', marginBottom: '14px' }}
					sx={{ display: { xs: 'none', sm: 'flex' } }}
				>
					<>{renderFilters()}</>
				</Grid>
				<Badge
					color="primary"
					variant="dot"
					sx={{
						display: { xs: 'block', sm: 'none' },
						width: '100%',
						'.MuiBadge-badge': { width: '10px', height: '10px' },
					}}
					invisible={nbMoreFilters > 0 ? false : true}
				>
					{!isMobile ? (
						<Button
							onClick={() => setOpenFilters(true)}
							startIcon={<AppCustomIcon tag="more_filters" />}
							color="secondary"
							variant="contained"
							size="small"
							fullWidth
						>
							{t(investmentFilterLang.btn_more_filters.tag)}
						</Button>
					) : (
						<Button
							onClick={() => setOpenFilters(true)}
							color="secondary"
							variant="contained"
							fullWidth
							size="small"
							sx={{}}
						>
							<AppCustomIcon tag="more_filters" />
						</Button>
					)}
				</Badge>
				<CAppDialog
					open={openFilters}
					onClose={() => setOpenFilters(false)}
					title={t(investmentFilterLang.dialog_filters_title.tag)}
					actions={
						<Grid container spacing={3} alignItems="center" justifyContent="center">
							<Grid item sm={3} style={{ textAlign: 'center' }}>
								<Button
									variant="contained"
									color="secondary"
									fullWidth
									startIcon={<CheckOutlined />}
									onClick={() => setOpenFilters(false)}
								>
									{t(investmentFilterLang.dialog_btn_apply.tag)}
								</Button>
							</Grid>
						</Grid>
					}
				>
					<>
						<Grid container spacing={3}>
							<>{renderFilters()}</>
						</Grid>
					</>
				</CAppDialog>
			</Box>
		</>
	);
};

export default FDocumentFilters;
